import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createEnrollment,
  getAllEnrollments,
  getChildEnrollments,
  getEnrollmentById,
  updateEnrollment,
  deleteEnrollment,
  getEnrollmentGroups,
  getEnrollmentsByClassId,
} from './enrollmentApi';

// Default retry configuration
const defaultRetryConfig = {
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

/**
 * Hook to fetch all enrollments
 */
export const useGetAllEnrollments = () => {
  return useQuery({
    queryKey: ['enrollments'],
    queryFn: async () => {
      try {
        const data = await getAllEnrollments();
        return data;
      } catch (error) {
        console.error('Error in useGetAllEnrollments:', error);
        toast.error('Failed to fetch enrollments');
        throw error;
      }
    },
    ...defaultRetryConfig,
  });
};

/**
 * Hook to fetch all enrollment groups
 */
export const useGetEnrollmentGroups = () => {
  return useQuery({
    queryKey: ['enrollmentGroups'],
    queryFn: async () => {
      try {
        const data = await getEnrollmentGroups();

        const transformedData = data.data.map((item) => {
          return item.groups.map((group) => {
            return {
              name: `${item.name} - ${group.className}`,
              participants: group.childIds.map((childId) => {
                return { id: childId };
              }),
            };
          });
        });

        let newResponse = [];

        function flatten(transformedData) {
          for (const item of transformedData) {
            if (item.name) {
              newResponse.push(item);
            } else {
              flatten(item);
            }
          }
        }

        flatten(transformedData);

        return newResponse;
      } catch (error) {
        if (!error.data) {
          // toast.info('No enrollment groups available');
          return [];
        } else {
          console.error('Error in useGetEnrollmentGroups:', error);
          toast.error('Failed to fetch enrollment groups');
          throw error;
        }
      }
    },
    ...defaultRetryConfig,
  });
};

/**
 * Hook to fetch enrollments for a specific child
 * @param {string} childId - The ID of the child
 */
export const useGetChildEnrollments = (childId) => {
  return useQuery({
    queryKey: ['enrollments', 'child', childId],
    queryFn: () => getChildEnrollments(childId),
    enabled: !!childId,
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : `Failed to fetch enrollments: ${error.message || 'Unknown error'}`;
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch enrollments for a specific class
 * @param {string} classId - The ID of the class
 */
export const useGetClassEnrollments = (classId) => {
  return useQuery({
    queryKey: ['enrollments', 'class', classId],
    queryFn: () => getEnrollmentsByClassId(classId),
    enabled: !!classId,
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : `Failed to fetch enrollments: ${error.message || 'Unknown error'}`;
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch a specific enrollment by ID
 * @param {string} enrollmentId - The ID of the enrollment
 */
export const useGetEnrollmentById = (enrollmentId) => {
  return useQuery({
    queryKey: ['enrollments', enrollmentId],
    queryFn: () => getEnrollmentById(enrollmentId),
    enabled: !!enrollmentId,
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : `Failed to fetch enrollment: ${error.message || 'Unknown error'}`;
      toast.error(message);
    },
  });
};

/**
 * Hook to create a new enrollment
 */
export const useCreateEnrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEnrollment,
    retry: 1,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      toast.success('Enrollment created successfully!');
      return data;
    },
    onError: (error) => {
      console.error('Error creating enrollment:', error);
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : error.response?.data?.message || 'Failed to create enrollment';
      toast.error(message);
    },
  });
};

/**
 * Hook to update an existing enrollment
 */
export const useUpdateEnrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ enrollmentId, data }) =>
      updateEnrollment(enrollmentId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({
        queryKey: ['enrollments', variables.enrollmentId],
      });
      queryClient.invalidateQueries({ queryKey: ['enrollments', 'child'] });
      toast.success('Enrollment updated successfully!');
      return data;
    },
    onError: (error) => {
      console.error('Error updating enrollment:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update enrollment'
      );
    },
  });
};

/**
 * Hook to delete an enrollment
 */
export const useDeleteEnrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEnrollment,
    onSuccess: (data, enrollmentId) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({
        queryKey: ['enrollments', enrollmentId],
      });
      queryClient.invalidateQueries({ queryKey: ['enrollments', 'child'] });
      toast.success('Enrollment deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting enrollment:', error);
      toast.error(
        error.response?.data?.message || 'Failed to delete enrollment'
      );
    },
  });
};

/**
 * Combined hook that provides all enrollment-related operations
 * @returns {Object} Methods for managing enrollments
 */
export const useEnrollments = () => {
  const createEnrollmentMutation = useCreateEnrollment();
  const updateEnrollmentMutation = useUpdateEnrollment();
  const deleteEnrollmentMutation = useDeleteEnrollment();
  const { data: enrollmentsData, isLoading } = useGetAllEnrollments();

  // Safely access enrollments data with proper fallbacks
  const enrollments = Array.isArray(enrollmentsData)
    ? enrollmentsData
    : Array.isArray(enrollmentsData?.data)
    ? enrollmentsData.data
    : [];

  return {
    enrollments,
    isLoading,
    createEnrollment: createEnrollmentMutation.mutate,
    updateEnrollment: updateEnrollmentMutation.mutate,
    deleteEnrollment: deleteEnrollmentMutation.mutate,
    isPending: {
      create: createEnrollmentMutation.isPending,
      update: updateEnrollmentMutation.isPending,
      delete: deleteEnrollmentMutation.isPending,
    },
  };
};
